/**
 * Normaliza `modules.behavior.heartbeat` (único contrato oficial).
 * Sem fallback, sem aliases legacy.
 */

import type { BehaviorModuleConfig } from '@/config/types/modules/behavior';

import type { HeartbeatConfig } from './contracts/heartbeat';

export const DEFAULT_HEARTBEAT_CONFIG: HeartbeatConfig = {
  enabled: false,
  intervalMs: 15_000,
  hiddenIntervalMs: 60_000,
  emitWhenHidden: false,
  emitWhenIdle: true,
  includeSnapshot: true,
  fireImmediately: false,
};

/** Intervalo mínimo seguro (ms). */
export const HEARTBEAT_MIN_INTERVAL_MS = 1_000;

type PartialHeartbeat = Partial<{
  enabled: boolean;
  intervalMs: number;
  hiddenIntervalMs: number;
  emitWhenHidden: boolean;
  emitWhenIdle: boolean;
  includeSnapshot: boolean;
  fireImmediately: boolean;
  minActivityWindowMs: number;
}>;

/** Lê e normaliza exclusivamente `behavior.heartbeat`. */
export function resolveHeartbeatConfig(
  behavior: BehaviorModuleConfig
): HeartbeatConfig {
  return normalizeHeartbeatConfig(behavior.heartbeat);
}

export function normalizeHeartbeatConfig(
  raw: PartialHeartbeat | HeartbeatConfig | undefined
): HeartbeatConfig {
  const enabled = raw?.enabled === true;

  let intervalMs =
    typeof raw?.intervalMs === 'number' && Number.isFinite(raw.intervalMs)
      ? Math.floor(raw.intervalMs)
      : DEFAULT_HEARTBEAT_CONFIG.intervalMs;

  if (intervalMs < HEARTBEAT_MIN_INTERVAL_MS) {
    intervalMs = HEARTBEAT_MIN_INTERVAL_MS;
  }

  let hiddenIntervalMs =
    typeof raw?.hiddenIntervalMs === 'number' &&
    Number.isFinite(raw.hiddenIntervalMs)
      ? Math.floor(raw.hiddenIntervalMs)
      : DEFAULT_HEARTBEAT_CONFIG.hiddenIntervalMs;

  if (hiddenIntervalMs < HEARTBEAT_MIN_INTERVAL_MS) {
    hiddenIntervalMs = HEARTBEAT_MIN_INTERVAL_MS;
  }

  const result: HeartbeatConfig = {
    enabled,
    intervalMs,
    hiddenIntervalMs,
    emitWhenHidden: raw?.emitWhenHidden === true,
    emitWhenIdle: raw?.emitWhenIdle !== false,
    includeSnapshot: raw?.includeSnapshot !== false,
    fireImmediately: raw?.fireImmediately === true,
  };

  if (
    typeof raw?.minActivityWindowMs === 'number' &&
    Number.isFinite(raw.minActivityWindowMs) &&
    raw.minActivityWindowMs >= 0
  ) {
    return {
      ...result,
      minActivityWindowMs: Math.floor(raw.minActivityWindowMs),
    };
  }

  return result;
}
